import { useEffect, useState } from 'react';
import { NavLink, Routes, Route } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../lib/firebase.js';
import { useAuth } from '../lib/auth.jsx';
import Teams from './admin/Teams.jsx';
import Skills from './admin/Skills.jsx';
import Responders from './admin/Responders.jsx';
import EscalationChains from './admin/EscalationChains.jsx';
import Customers from './admin/Customers.jsx';
import Tickets from './admin/Tickets.jsx';
import Settings from './admin/Settings.jsx';
import Coverage from './admin/Coverage.jsx';
import Analytics from './admin/Analytics.jsx';
import Billing from './admin/Billing.jsx';
import Assets from './admin/Assets.jsx';
import KnowledgeBase from './admin/KnowledgeBase.jsx';
import People from './admin/People.jsx';
import AccessPoints from './admin/AccessPoints.jsx';
import EmailIntake from './admin/EmailIntake.jsx';
import AiPerformance from './admin/AiPerformance.jsx';

// Admin shell. Each section is currently a placeholder — they're filled in by phases 2–4.

export default function AdminDashboard() {
  const { claims } = useAuth();
  const [companyName, setCompanyName] = useState('');

  useEffect(() => {
    if (!claims?.vendor_id) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'vendors', claims.vendor_id));
        if (!cancelled && snap.exists()) {
          setCompanyName(snap.data().name || '');
        }
      } catch (e) {
        // Multi-tenant rule failure here would be a serious bug — log it.
        console.error('Failed to load vendor doc', e);
      }
    })();
    return () => { cancelled = true; };
  }, [claims?.vendor_id]);

  async function handleLogout() {
    await signOut(auth);
  }

  return (
    <div className="admin">
      <aside className="admin-sidebar">
        <div className="brand">
          {/* Logo with text fallback if the PNG is missing */}
          <img
            src="/giraops-logo.png"
            alt="Giraops"
            className="brand-logo-img"
            onError={(e) => {
              // Fall back to the text we removed by hiding the image and revealing the sibling.
              e.target.style.display = 'none';
              const fallback = e.target.nextElementSibling;
              if (fallback) fallback.style.display = 'inline';
            }}
          />
          <span style={{ display: 'none' }}>Giraops</span>
        </div>
        <nav>
          {/* OPERATIONS — what an admin looks at daily (live state of the service) */}
          <div className="admin-nav-section-label">Operations</div>
          <NavLink to="/admin" end>Dashboard</NavLink>
          <NavLink to="/admin/tickets">Tickets</NavLink>
          <NavLink to="/admin/coverage">Coverage</NavLink>
          <NavLink to="/admin/analytics">Analytics</NavLink>
          <NavLink to="/admin/ai">AI performance</NavLink>

          {/* SETUP — configure-once concepts. Grouped because they're siblings
              ("how is my organization structured") rather than daily operations. */}
          <div className="admin-nav-section-label">Setup</div>
          <NavLink to="/admin/teams">Teams</NavLink>
          <NavLink to="/admin/skills">Skill Categories</NavLink>
          <NavLink to="/admin/assets">Assets</NavLink>
          <NavLink to="/admin/qr-access">QR access</NavLink>
          <NavLink to="/admin/email-intake">Email intake</NavLink>
          <NavLink to="/admin/knowledge">Knowledge Base</NavLink>
          <NavLink to="/admin/people">People</NavLink>
          <NavLink to="/admin/escalation">Escalation Chains</NavLink>

          {/* SYSTEM — vendor-level toggles, integration settings, alerts, assets */}
          <div className="admin-nav-section-label">System</div>
          <NavLink to="/admin/settings">Settings</NavLink>
          <NavLink to="/admin/billing">Billing</NavLink>
        </nav>
      </aside>

      <main className="admin-main">
        <header className="admin-header">
          <div className="company-name">{companyName || 'Loading…'}</div>
          <button onClick={handleLogout}>Sign out</button>
        </header>

        <section className="admin-content">
          <Routes>
            <Route path="/" element={<DashboardHome />} />
            <Route path="teams" element={<Teams />} />
            <Route path="skills" element={<Skills />} />
            <Route path="people" element={<People />} />
            {/* Legacy routes kept so old links/bookmarks still work, but the nav
                now points everyone to /people. */}
            <Route path="responders" element={<Responders />} />
            <Route path="customers" element={<Customers />} />
            <Route path="escalation" element={<EscalationChains />} />
            <Route path="tickets" element={<Tickets />} />
            <Route path="coverage" element={<Coverage />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="ai" element={<AiPerformance />} />
            <Route path="assets" element={<Assets />} />
            <Route path="qr-access" element={<AccessPoints />} />
            <Route path="email-intake" element={<EmailIntake />} />
            <Route path="knowledge" element={<KnowledgeBase />} />
            <Route path="settings" element={<Settings />} />
            <Route path="billing" element={<Billing />} />
          </Routes>
        </section>
      </main>
    </div>
  );
}

function DashboardHome() {
  return (
    <>
      <h2>Dashboard</h2>
      <div className="placeholder">
        Your service desk overview will appear here once you've added a team and your first ticket comes in.
        For now, head to <strong>Teams</strong> to set things up.
      </div>
    </>
  );
}
